import { cents, sum, type Cents } from '../money.js';
import type { Item } from '../types.js';
import type { Settlement } from '../split/settle.js';

/**
 * Encode a finished split into a URL fragment.
 *
 * Everything after `#` is never transmitted to a server, so the whole share
 * feature needs no database, no rows and no expiry — and a shared split stays
 * as private as the message carrying it. That is a real advantage over
 * competitors who persist splits server-side.
 *
 * The cost is a hard size ceiling. A restaurant bill fits comfortably; a
 * 40-item supermarket receipt will not, and `ShareTooLargeError` says so rather
 * than emitting a link that silently truncates in some chat clients.
 */

/** What a recipient sees. Deliberately less than a full `Settlement`. */
export interface SharedSplit {
  currency: string;
  merchant?: string;
  grandTotal: Cents;
  lines: Array<{ name: string; total: Cents; items: string[] }>;
}

/**
 * Conservative ceiling. Browsers handle far more, but chat clients, QR codes
 * and link previewers are the real constraint and they are less forgiving.
 */
export const MAX_FRAGMENT_LENGTH = 1800;

export class ShareTooLargeError extends Error {
  constructor(readonly length: number) {
    super(
      `split is too large to put in a link (${length} of ${MAX_FRAGMENT_LENGTH} characters)`,
    );
    this.name = 'ShareTooLargeError';
  }
}

export class ShareDecodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShareDecodeError';
  }
}

/**
 * Wire shape. Keys are single letters because every byte survives into the URL
 * after base64's 33% expansion.
 */
interface Wire {
  c: string;
  m?: string;
  /** [name, totalCents] per person. */
  l: Array<[string, number]>;
  /** Item names per person, parallel to `l`. Omitted when empty. */
  b?: string[][];
}

/**
 * Version marker, kept *outside* the compressed payload so that the compression
 * scheme itself can change later without old links becoming undecodable.
 */
const VERSION = '1';

const toBase64Url = (bytes: Uint8Array): string => {
  let binary = '';
  // Chunked to avoid blowing the argument limit on large payloads.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromBase64Url = (text: string): Uint8Array => {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

async function pipeThrough(
  bytes: Uint8Array,
  // Compression streams accept any BufferSource on the way in and always emit
  // Uint8Array, so the pair is asymmetric.
  transform: TransformStream<BufferSource, Uint8Array>,
): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(transform);
  const buffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(buffer);
}

export async function encodeShareState(
  settlement: Settlement,
  items: Item[] = [],
): Promise<string> {
  const names = new Map(items.map((item) => [item.id, item.name]));

  const breakdown = settlement.lines.map((line) => [
    ...new Set(
      line.items
        .map((id) => names.get(id))
        .filter((name): name is string => name !== undefined),
    ),
  ]);

  const wire: Wire = {
    c: settlement.currency,
    ...(settlement.merchant !== undefined ? { m: settlement.merchant } : {}),
    l: settlement.lines.map((line) => [line.name, line.total] as [string, number]),
    ...(breakdown.some((entry) => entry.length > 0) ? { b: breakdown } : {}),
  };

  const json = new TextEncoder().encode(JSON.stringify(wire));
  const compressed = await pipeThrough(json, new CompressionStream('deflate-raw'));
  const encoded = VERSION + toBase64Url(compressed);

  if (encoded.length > MAX_FRAGMENT_LENGTH) throw new ShareTooLargeError(encoded.length);
  return encoded;
}

export async function decodeShareState(encoded: string): Promise<SharedSplit> {
  const trimmed = encoded.startsWith('#') ? encoded.slice(1) : encoded;
  if (trimmed.length === 0) throw new ShareDecodeError('link carries no split');

  const version = trimmed[0];
  if (version !== VERSION) {
    throw new ShareDecodeError(`unsupported link version "${version ?? ''}"`);
  }

  let wire: Wire;
  try {
    const bytes = fromBase64Url(trimmed.slice(1));
    const inflated = await pipeThrough(bytes, new DecompressionStream('deflate-raw'));
    wire = JSON.parse(new TextDecoder().decode(inflated)) as Wire;
  } catch {
    throw new ShareDecodeError('link is damaged or incomplete');
  }

  if (typeof wire?.c !== 'string' || !Array.isArray(wire.l)) {
    throw new ShareDecodeError('link is damaged or incomplete');
  }

  const lines = wire.l.map(([name, total], index) => {
    if (typeof name !== 'string' || !Number.isInteger(total)) {
      throw new ShareDecodeError('link is damaged or incomplete');
    }
    return { name, total: cents(total), items: wire.b?.[index] ?? [] };
  });

  return {
    currency: wire.c,
    ...(wire.m !== undefined ? { merchant: wire.m } : {}),
    // Derived rather than stored: one less field to keep consistent, and it
    // cannot disagree with the lines it is meant to summarise.
    grandTotal: sum(lines.map((line) => line.total)),
    lines,
  };
}
